import { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

import { useLingui } from '@lingui/react/macro';

/**
 * SlideDrawer — drawer trượt từ phải sang trái.
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - width: number (px) | string (css) — default 960
 *  - title: string | ReactNode — hiển thị trong header (optional)
 *  - headerExtra: ReactNode — button row bên phải của title (optional)
 *  - footer: ReactNode — sticky footer (optional)
 *  - children: body
 *  - closeOnBackdrop: bool — default true
 *  - closeOnEsc: bool — default true
 *  - leftRail: ReactNode — cụm điều khiển nổi bám mép trái panel (vd nút trước/sau)
 *  - scrollResetKey: any — đổi giá trị ⇒ cuộn body về đầu (đổi bản ghi trong cùng drawer)
 */
export default function SlideDrawer({
  open,
  onClose,
  width = 960,
  title,
  headerExtra,
  headerHeight = 48,
  hideHeader = false,
  footer,
  children,
  closeOnBackdrop = true,
  closeOnEsc = true,
  className = '',
  leftRail = null,
  scrollResetKey,
}) {
  const {
    t
  } = useLingui();

  const ref = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open || !closeOnEsc) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, closeOnEsc, onClose]);

  // Disable body scroll when open — đồng thời pad-right để bù scrollbar bị mất → tránh giật content
  useEffect(() => {
    if (!open) return;
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    const orig = { overflow: document.body.style.overflow, paddingRight: document.body.style.paddingRight };
    document.body.style.overflow = 'hidden';
    if (scrollbarW > 0) document.body.style.paddingRight = `${scrollbarW}px`;
    return () => {
      document.body.style.overflow = orig.overflow;
      document.body.style.paddingRight = orig.paddingRight;
    };
  }, [open]);

  // Đổi bản ghi mà không đóng drawer → body vẫn giữ scrollTop cũ, kéo user vào giữa trang mới.
  useEffect(() => {
    if (scrollResetKey === undefined) return;
    bodyRef.current?.scrollTo({ top: 0 });
  }, [scrollResetKey]);

  const widthCss = typeof width === 'number' ? `${width}px` : width;

  return ReactDOM.createPortal(
    <div className={`fixed inset-0 z-[9999] ${open ? 'pointer-events-auto' : 'pointer-events-none'}`} aria-hidden={!open}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0'}`}
        onClick={() => { if (closeOnBackdrop) onClose?.(); }}
      />
      {/* Drawer — mobile (<md): full screen; desktop: width theo prop */}
      <div
        ref={ref}
        className={`absolute top-0 right-0 h-full bg-white shadow-2xl md:border-l md:border-gray-200 flex flex-col transition-transform duration-250 ease-out max-md:!w-full max-md:!max-w-none ${open ? 'translate-x-0' : 'translate-x-full'} ${className}`}
        style={{ width: widthCss, maxWidth: '95vw', transitionDuration: '250ms' }}
      >
        {/* Rail điều khiển bám mép trái — nằm TRONG panel để trượt cùng panel và không ăn click backdrop */}
        {leftRail}
        {/* Header — height theo prop headerHeight, shadow nhẹ thay border */}
        {!hideHeader && (title || headerExtra) && (
          <div className="flex-shrink-0 flex items-center gap-2 px-4 bg-white shadow-[0_1px_0_rgba(0,0,0,0.05)] z-10" style={{ height: `${headerHeight}px` }}>
            <div className="flex-1 min-w-0 text-sm font-semibold text-gray-900 truncate">
              {title}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {headerExtra}
              <button
                onClick={onClose}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition"
                title={t`Đóng (Esc)`}
                aria-label={t`Đóng`}
              >
                <X size={15} />
              </button>
            </div>
          </div>
        )}
        {/* Body */}
        <div ref={bodyRef} className="flex-1 min-h-0 overflow-y-auto">
          {children}
        </div>
        {/* Footer */}
        {footer && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
